import { AppConfig } from '@core/domain/AppConfig.ts';
import type { LocalSqliteRuntime } from '@src/modules/shared/local-sqlite/domain/LocalSqliteRuntime.ts';
import { readBrowserStorageEstimate } from '@src/modules/shared/local-sqlite/domain/browserStorageEstimate.ts';
import { PuntosEntregaApplicationService } from '../../application/PuntosEntregaApplicationService.ts';
import {
  loadPuntosEntregaSqlDatabaseFromSnapshotOnly,
  openPuntosEntregaSqlConnectionFromSnapshot,
} from '../driven/loadPuntosEntregaSqlDatabase.ts';
import { SqlJsPuntosEntregaRepository } from '../driven/SqlJsPuntosEntregaRepository.ts';

let sqlBootPromise: Promise<PuntosEntregaApplicationService> | undefined;
let ephemeralShellPromise: Promise<PuntosEntregaApplicationService> | undefined;

/**
 * Initializes the local SQLite database and returns the delivery points application service.
 */
export async function bootstrapPuntosEntregaSql(runtime: LocalSqliteRuntime): Promise<PuntosEntregaApplicationService> {
  if (!AppConfig.useLocalSqliteClient()) {
    ephemeralShellPromise ??= (async () => {
      const db = await loadPuntosEntregaSqlDatabaseFromSnapshotOnly(AppConfig.getLocalSqliteSnapshotUrl());
      return new PuntosEntregaApplicationService(
        new SqlJsPuntosEntregaRepository(db, AppConfig.getFilesPublicPrefix(), () => {
          /* no IndexedDB in snapshot-only mode */
        }),
      );
    })().catch((e) => {
      ephemeralShellPromise = undefined;
      runtime.signalConnectionFailed(e instanceof Error ? e.message : String(e));
      throw e;
    });
    return ephemeralShellPromise;
  }
  sqlBootPromise ??= (async () => {
    const { db, persist, flushPersist, hasPendingWrite } = await openPuntosEntregaSqlConnectionFromSnapshot(
      AppConfig.getLocalSqliteSnapshotUrl(),
    );
    runtime.setSession(db, persist, flushPersist, hasPendingWrite);
    const localPersist = {
      flushAfterMutation: () => runtime.flushPersist(),
      readStorageEstimate: readBrowserStorageEstimate,
    };
    return new PuntosEntregaApplicationService(
      new SqlJsPuntosEntregaRepository(db, AppConfig.getFilesPublicPrefix(), persist),
      localPersist,
    );
  })().catch((e) => {
    sqlBootPromise = undefined;
    runtime.signalConnectionFailed(e instanceof Error ? e.message : String(e));
    throw e;
  });
  return sqlBootPromise;
}
